import type { Project, ProjectCategory, PortfolioData } from '@/types/portfolio';
import { portfolioData } from '@/data/projects';
import { 
  formatCategoryName, 
  filterProjectsByCategory, 
  navigateTo,
  addEventListenerWithCleanup 
} from '@/utils/helpers';

/**
 * Portfolio Home Page - Dynamically loads and displays projects
 */
class PortfolioHome {
  private projectData: PortfolioData | null = null;
  private allProjects: Project[] = [];
  private filteredProjects: Project[] = [];
  private _currentFilter: ProjectCategory | 'all' = 'all';

  get currentFilter(): ProjectCategory | 'all' {
    return this._currentFilter;
  }
  private cleanupFunctions: (() => void)[] = [];

  /**
   * Initialize the portfolio home page
   */
  async init(): Promise<void> {
    try {
      await this.loadProjectData();
      this.renderProjects();
      this.setupFiltering();
      this.setupProjectNavigation();
    } catch (error) {
      console.error('Failed to initialize portfolio:', error);
      this.showError();
    }
  }

  /**
   * Load project data
   */
  private async loadProjectData(): Promise<PortfolioData> {
    // Use embedded project data directly
    this.projectData = portfolioData;
    this.allProjects = this.projectData.projects;
    this.filteredProjects = [...this.allProjects];
    return this.projectData;
  }

  /**
   * Render all projects in the grid
   */
  private renderProjects(): void {
    const grid = document.getElementById('projectsGrid');
    const loadingElement = document.getElementById('loading');
    
    if (!grid) {
      console.error('Projects grid element not found');
      return;
    }

    if (loadingElement) {
      loadingElement.remove();
    }

    // Clear existing content
    grid.innerHTML = '';

    if (this.filteredProjects.length === 0) {
      grid.innerHTML = '<div class="no-projects">No projects found for this category.</div>';
      return;
    }

    this.filteredProjects.forEach(project => {
      const projectCard = this.createProjectCard(project);
      grid.appendChild(projectCard);
    });
  }

  /**
   * Create a project card element
   */
  private createProjectCard(project: Project): HTMLElement {
    const card = document.createElement('div');
    card.className = 'project-card';
    card.setAttribute('data-categories', project.categories.join(' '));
    card.setAttribute('data-project-id', project.id);

    const categoryTags = project.categories
      .map(category => `<span class="project-category-tag">${formatCategoryName(category)}</span>`)
      .join('');

    card.innerHTML = `
      <h3 class="project-title">${project.title}</h3>
      <p class="project-description">${project.description}</p>
      <div class="project-categories">${categoryTags}</div>
    `;

    return card;
  }


  /**
   * Set up project filtering functionality
   */
  private setupFiltering(): void {
    const categoryButtons = document.querySelectorAll('.category-btn');
    
    categoryButtons.forEach(button => {
      const cleanup = addEventListenerWithCleanup(button, 'click', (e) => {
        const target = e.target as HTMLElement;
        const selectedCategory = target.getAttribute('data-category') as ProjectCategory | 'all';
        
        // Update active button
        categoryButtons.forEach(btn => btn.classList.remove('active'));
        target.classList.add('active');
        
        // Filter projects
        this.filterProjects(selectedCategory);
      });
      
      this.cleanupFunctions.push(cleanup);
    });
  }

  /**
   * Filter projects by category
   */
  private filterProjects(category: ProjectCategory | 'all'): void {
    this._currentFilter = category;
    this.filteredProjects = filterProjectsByCategory(this.allProjects, category);
    this.renderProjects();
    this.setupProjectNavigation(); // Re-setup navigation for new cards
  }

  /**
   * Set up project card navigation
   */
  private setupProjectNavigation(): void {
    const projectCards = document.querySelectorAll('.project-card');
    
    projectCards.forEach(card => {
      // Make entire card clickable
      const cardElement = card as HTMLElement;
      cardElement.style.cursor = 'pointer';
      
      const cleanup = addEventListenerWithCleanup(cardElement, 'click', (e) => {
        const target = e.target as HTMLElement;
        
        // Don't navigate if clicking on a link
        if (target.tagName === 'A' || target.closest('a')) {
          return;
        }

        const projectId = cardElement.getAttribute('data-project-id');
        if (projectId) {
          navigateTo(`project.html?id=${projectId}`);
        }
      });
      
      this.cleanupFunctions.push(cleanup);
    });
  }

  /**
   * Show error message if loading fails
   */
  private showError(): void {
    const grid = document.getElementById('projectsGrid');
    const loadingElement = document.getElementById('loading');
    
    if (!grid) return;
    
    if (loadingElement) {
      loadingElement.remove();
    }

    grid.innerHTML = `
      <div class="error-message">
        <h3>Unable to load projects</h3>
        <p>There was an error loading the project data. Please try refreshing the page.</p>
      </div>
    `;
  }

  /**
   * Cleanup event listeners
   */
  destroy(): void {
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];
  }
}

// Initialize portfolio when page loads
let portfolioInstance: PortfolioHome | null = null;

document.addEventListener('DOMContentLoaded', () => {
  portfolioInstance = new PortfolioHome();
  portfolioInstance.init();
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (portfolioInstance) {
    portfolioInstance.destroy();
  }
});